import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useDraftMode } from "../contexts/DraftModeContext";
import { useHospitalData } from "../contexts/HospitalDataProvider";
import { backendService } from "../services/backend.service";
import { trackEvent } from "../utils/analytics";
import { copyDayData, findScheduleSlot } from "../utils/departmentWeeks";
import type { CensusGrouping, CensusOverride, ChangesSource, Day, HospitalData, ScheduleDay } from "../utils/models";
import { toIsoDate } from "../utils/scheduleWeeks";
import { invalidateSchedule, patchScheduleDay } from "./scheduleCacheWrites";
import { useScheduleCacheEnabled } from "./useScheduleWeekRules";

/** Applies `update` to the census day of `shiftType` on `date`, when that day is loaded. */
// Copies the day it changes: the staffing pages rebuild a week only when its arrays change.
const withCensusDay = (data: HospitalData, date: string, shiftType: string, update: (day: Day) => void): HospitalData => {
	const slot = findScheduleSlot(data.weeks, DateTime.fromISO(date), shiftType);
	if (!slot) return data;
	const { day, newData } = copyDayData(data, slot.weekIndex, slot.shiftIndex, slot.dayIndex);
	update(day);
	return newData;
};

export const useHandleCensus = () => {
	const { afterScheduleWrite, chosenDepartment, showErrorSnackbar, setHospitalData } = useHospitalData();
	const { isDraftMode } = useDraftMode();
	const queryClient = useQueryClient();
	const isCacheEnabled = useScheduleCacheEnabled();

	// With the cache on the screens that read it show the edit at once too, a draft's edit included.
	const patchCachedCensus = async (date: string, update: (day: ScheduleDay) => ScheduleDay): Promise<void> => {
		if (isCacheEnabled) await patchScheduleDay(queryClient, chosenDepartment.department_id, DateTime.fromISO(date), update);
	};

	// The save failed: the cached week reloads from the server, rather than going back to a copy taken before the edit,
	// which would also undo a later edit of that week that did save.
	const saveOrReload = async (date: string, save: () => Promise<unknown>): Promise<void> => {
		try {
			await save();
		} catch (error) {
			if (isCacheEnabled) await invalidateSchedule(queryClient, chosenDepartment.department_id, { dates: [DateTime.fromISO(date)] });
			throw error;
		}
	};

	const {
		mutateAsync: updateCensusOverride,
		isPending: isUpdatingCensus,
		error: updateCensusError,
	} = useMutation({
		mutationFn: async ({
			date,
			shiftType,
			overrideValue,
			note,
			source,
			locationCensus,
		}: {
			date: string;
			shiftType: string;
			overrideValue: number;
			note?: string;
			source: ChangesSource;
			locationCensus?: CensusGrouping;
		}) => {
			const optimisticOverride = (current: CensusOverride | null | undefined): CensusOverride => ({
				override_value: overrideValue,
				note: note || "",
				user_id: current?.user_id || "optimistic",
				updated_at: new Date().toISOString(),
			});
			// Update local state optimistically
			setHospitalData((prev) =>
				withCensusDay(prev, date, shiftType, (day) => {
					day.census_override = optimisticOverride(day.census_override);
					// Retyping answers the "EMR replaced your number" notice.
					day.census_replacement = undefined;
					if (locationCensus) {
						day.census_grouping = locationCensus;
					}
				}),
			);
			// The cached day holds one census for all its shifts.
			await patchCachedCensus(date, (day) => ({
				...day,
				census_override: optimisticOverride(day.census_override),
				...(locationCensus && { census_grouping: locationCensus }),
			}));

			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { success: true };
			}

			await saveOrReload(date, () =>
				backendService.createOrUpdateCensusOverride({
					department_id: chosenDepartment.department_id,
					date,
					shift_type: shiftType,
					override_value: overrideValue,
					note,
					source,
					location_census: locationCensus,
				}),
			);

			trackEvent("Edit census menu", {
				action: "update census",
				date,
				shift_type: shiftType,
				census: overrideValue,
			});

			if (!isDraftMode) {
				await afterScheduleWrite({ dates: [DateTime.fromISO(date)] });
			}

			return { success: true };
		},
		onError: (error, variables) => {
			trackEvent("Edit census menu", {
				action: "update census failed",
				date: variables.date,
				shift_type: variables.shiftType,
				census: variables.overrideValue,
			});
			showErrorSnackbar("Failed to update census", { date: variables.date, shift_type: variables.shiftType }, error);
		},
	});

	const {
		mutateAsync: deleteCensusOverride,
		isPending: isDeletingCensus,
		error: deleteCensusError,
	} = useMutation({
		mutationFn: async ({ date, shiftType, source }: { date: string; shiftType: string; source: ChangesSource }) => {
			// Update local state optimistically
			setHospitalData((prev) =>
				withCensusDay(prev, date, shiftType, (day) => {
					day.census_override = undefined;
				}),
			);
			await patchCachedCensus(date, (day) => ({ ...day, census_override: null }));

			// If draft mode is enabled, skip backend call (auto-tracking will detect the change)
			if (isDraftMode) {
				return { success: true };
			}

			// Not in draft mode, call the API
			await saveOrReload(date, () =>
				backendService.deleteCensusOverride({
					department_id: chosenDepartment.department_id,
					date,
					shift_type: shiftType,
					source,
				}),
			);

			trackEvent("Edit census menu", {
				action: "delete census override",
				date,
				shift_type: shiftType,
			});

			if (!isDraftMode) {
				await afterScheduleWrite({ dates: [DateTime.fromISO(date)] });
			}

			return { success: true };
		},
		onError: (error, variables) => {
			trackEvent("Edit census menu", {
				action: "delete census override failed",
				date: variables.date,
				shift_type: variables.shiftType,
			});
			showErrorSnackbar("Failed to delete census override", { date: variables.date, shift_type: variables.shiftType }, error);
		},
	});

	const { mutateAsync: acceptCensusReplacement, isPending: isAcceptingCensus } = useMutation({
		mutationFn: async ({ date }: { date: string }) => {
			// Matched by date: a shift row skips the days it has no staffing for, so a position differs per row.
			const isOnDate = (day: Day): boolean => toIsoDate(day.date) === date;
			setHospitalData((prev) => ({
				...prev,
				weeks: prev.weeks.map((week) =>
					week.weeklyShifts.some((shift) => shift.days.some(isOnDate))
						? {
								...week,
								weeklyShifts: week.weeklyShifts.map((shift) => ({
									...shift,
									days: shift.days.map((day) => (isOnDate(day) ? { ...day, census_replacement: undefined } : day)),
								})),
							}
						: week,
				),
			}));

			// Saved even in draft mode: the notice is not schedule data, and the draft's change tracking
			// never sees it, so publishing the draft would not dismiss it.
			await backendService.dismissCensusReplacement({
				department_id: chosenDepartment.department_id,
				date,
			});
			// The notice is already gone locally. Reloading the schedule here rebuilds the weeks array and
			// the weekly grid snaps back to the route date, so Accept jumped to another week instead of
			// closing the menu on the day the admin was looking at.
			return { success: true };
		},
		onError: (error, variables) => {
			trackEvent("Edit census menu", { action: "accept census replacement failed", date: variables.date });
			showErrorSnackbar("Failed to accept the census replacement", { date: variables.date }, error);
		},
	});

	return {
		updateCensusOverride,
		deleteCensusOverride,
		acceptCensusReplacement,
		isLoading: {
			updateCensus: isUpdatingCensus,
			deleteCensus: isDeletingCensus,
			acceptCensus: isAcceptingCensus,
		},
		error: {
			updateCensus: updateCensusError,
			deleteCensus: deleteCensusError,
		},
	};
};
